import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Provider } from '@angular/core';
import {
  COMMONS_ENV,
  FirebaseObservableServiceFactory,
  FirestoreQueryService,
  FirestoreRemoteConfigsService,
  HttpService,
  NetworkService,
  ObservableQueryService,
  StorageService,
} from '@damofire/data-angular-commons';
import { IndexedService } from '@damofire/data-commons';
import {
  FirebaseProductionDrawingCuttingsService,
  FirebaseProductionDrawingsService,
  FirebaseProductionSalesOrdersService,
  ProductionDrawingCuttingsService,
  ProductionDrawingsService,
  ProductionSalesOrdersService,
} from '@damofire/data-production';
import { HttpProductColorService, ProductColorService } from '@damofire/data-repos';
import { AuthClaimsGuard, AuthDtoSignerService } from '@damofire/data-security';
import {
  DivisionGuardianMinionService,
  DivisionService,
  FirebaseDivisionService,
  FirebaseLinkService,
  FirebaseOrganizationService,
  FirebasePreferenceService,
  FirebaseUserService,
  LinkService,
  OrganizationService,
  PreferenceService,
  UserActionsService,
  UserService,
} from '@damofire/data-stas';
import { DocumentStorageService } from '@damofire/data-storage';
import {
  AssetRemoteService,
  AssetService,
  DefaultWarehouseSyncStepFactory,
  FirebaseAssetService,
  FirebaseInspectionService,
  FirebaseIssueCfgService,
  FirebaseIssueService,
  FirebaseNoteService,
  FirebaseObservableAssetService,
  FirebaseObservableInspectionService,
  FirebaseObservableIssueMediaService,
  FirebaseObservableIssueService,
  FirebaseObservableNoteService,
  FirebaseObservableRepairMediaService,
  FirebaseObservableRepairService,
  FirebaseObservableSavedSearchService,
  FirebaseRepairService,
  FirebaseSavedSearchService,
  FirebaseSystemDrawingService,
  FirebaseUprightService,
  FirebaseWarehouseLayoutTextService,
  FirebaseWarehouseObservableService,
  FirebaseWarehouseOutlineService,
  FirebaseWarehouseService,
  FirebaseWarehouseStateSnapshotService,
  CurrentWarehouseDataSource,
  HistoricalViewState,
  HistoryAwareAssetService,
  HistoryAwareIdbAssetService,
  HistoryAwareIdbIssueService,
  HistoryAwareIdbObservableAssetService,
  HistoryAwareIdbObservableIssueMediaService,
  HistoryAwareIdbObservableIssueService,
  HistoryAwareIdbObservableNoteService,
  HistoryAwareIdbSystemDrawingService,
  HistoryAwareIdbUprightService,
  HistoryAwareIdbWarehouseLayoutTextService,
  HistoryAwareIdbWarehouseOutlineService,
  HistoryAwareIdbWarehouseService,
  HistoryAwareIssueService,
  HistoryAwareObservableAssetService,
  HistoryAwareObservableIssueMediaService,
  HistoryAwareObservableIssueService,
  HistoryAwareObservableNoteService,
  HistoryAwareSystemDrawingService,
  HistoryAwareUprightService,
  HistoryAwareWarehouseService,
  HistoryAwareWarehouseLayoutTextService,
  HistoryAwareWarehouseOutlineService,
  SnapshotSessionStore,
  SnapshotWarehouseDataSource,
  WarehouseSourceService,
  IdbAssetService,
  IdbInspectionService,
  IdbIssueCfgService,
  IdbIssueService,
  IdbNoteService,
  IdbObservableAssetService,
  IdbObservableInspectionService,
  IdbObservableIssueMediaService,
  IdbObservableIssueService,
  IdbObservableNoteService,
  IdbObservableSavedSearchService,
  IdbSavedSearchService,
  IdbService,
  IdbSystemDrawingService,
  IdbUprightService,
  IdbWarehouseLayoutTextService,
  IdbWarehouseOutlineService,
  IdbWarehouseService,
  IndexedQueryService,
  InspectionRemoteService,
  InspectionReviewObservableService,
  InspectionReviewService,
  InspectionService,
  IssueCfgService,
  IssueMediaRemoteService,
  IssueRemoteService,
  IssueService,
  LayoutTextRemoteService,
  LockingIndexedService,
  NoteRemoteService,
  NoteService,
  ObservableAssetService,
  ObservableInspectionService,
  ObservableIssueMediaService,
  ObservableIssueService,
  ObservableNoteService,
  ObservableRepairMediaService,
  ObservableRepairService,
  ObservableSavedSearchService,
  ObservableSyncService,
  OutputSyncService,
  QuoteRequestService,
  RepairService,
  SavedSearchRemoteService,
  SavedSearchService,
  SnapshotService,
  SystemDrawingService,
  SystemRemoteService,
  UprightService,
  WarehouseInterceptorService,
  WarehouseLayoutTextService,
  WarehouseObservableService,
  WarehouseOutlineRemoteService,
  WarehouseOutlineService,
  WarehouseService,
  WarehouseStatsService,
  WarehouseSyncEventService,
  WarehouseSyncService,
  WarehouseSyncStepFactory,
} from '@damofire/data-watson';
import {
  ACCEPT_COOKIES_ENABLED,
  APP_TARGET,
  CheckForUpdateService,
  DialogService,
  EmitterService,
  FAQ_URL_EN,
  FAQ_URL_FR,
  OFFLINE_MODE,
  SERVICE_DESK_URL,
  SnackService,
  WEBSITE_URL,
} from '@damofire/uix-commons';
import { ConnectionService } from 'connection-service';
import packageInfo from '../../../../package.json';
import { environment } from '../environments/environment';

const SECURITY_PROVIDERS = [AuthDtoSignerService, AuthClaimsGuard];
const COMMON_PROVIDERS = [
  HttpService,
  NetworkService,
  StorageService,
  SnackService,
  DialogService,
  CheckForUpdateService,
  EmitterService,
  FirestoreRemoteConfigsService,
  { provide: ACCEPT_COOKIES_ENABLED, useValue: !environment.offline },
  { provide: WEBSITE_URL, useValue: environment.appConfig.websiteUrl },
  { provide: SERVICE_DESK_URL, useValue: environment.appConfig.helpDeskUrl },
  { provide: FAQ_URL_EN, useValue: environment.appConfig.faqUrlEn },
  { provide: FAQ_URL_FR, useValue: environment.appConfig.faqUrlFr },
  { provide: OFFLINE_MODE, useValue: environment.offline },
  { provide: APP_TARGET, useValue: environment.target },
  { provide: COMMONS_ENV, useValue: { ...environment, version: packageInfo.version } },
  { provide: ObservableQueryService, useClass: FirestoreQueryService },
  {
    provide: ConnectionService,
    useFactory: () => new ConnectionService({ enableHeartbeat: environment.offline, heartbeatUrl: environment.appConfig.heartbeatUrl }),
  },
];

const REPOS_PROVIDERS = [{ provide: ProductColorService, useClass: HttpProductColorService }];

const STAX_PROVIDERS = [
  DivisionGuardianMinionService,
  { provide: DivisionService, useClass: FirebaseDivisionService },
  { provide: OrganizationService, useClass: FirebaseOrganizationService },
  { provide: LinkService, useClass: FirebaseLinkService },
  { provide: UserService, useClass: FirebaseUserService },
  { provide: PreferenceService, useClass: FirebasePreferenceService },
  { provide: UserActionsService },
];

const PRODUCTION_PROVIDERS = [
  { provide: ProductionDrawingsService, useClass: FirebaseProductionDrawingsService },
  { provide: ProductionDrawingCuttingsService, useClass: FirebaseProductionDrawingCuttingsService },
  { provide: ProductionSalesOrdersService, useClass: FirebaseProductionSalesOrdersService },
];

const STORAGE_PROVIDERS = [{ provide: DocumentStorageService }];

const WATSON_PROVIDERS: Provider[] = [
  FirebaseIssueCfgService,
  InspectionReviewService,
  FirebaseIssueService,
  FirebaseAssetService,
  FirebaseInspectionService,
  FirebaseNoteService,
  FirebaseObservableIssueService,
  FirebaseObservableRepairService,
  FirebaseObservableAssetService,
  FirebaseObservableInspectionService,
  FirebaseObservableNoteService,
  FirebaseSavedSearchService,
  InspectionReviewObservableService,
  WarehouseStatsService,
  WarehouseInterceptorService,
  FirebaseSystemDrawingService,
  FirebaseWarehouseService,
  FirebaseObservableServiceFactory,
  FirebaseWarehouseLayoutTextService,
  FirebaseWarehouseOutlineService,
  CurrentWarehouseDataSource,
  HistoricalViewState,
  WarehouseSourceService,
  { provide: WarehouseObservableService, useClass: FirebaseWarehouseObservableService },
  { provide: RepairService, useClass: FirebaseRepairService },
  QuoteRequestService,
];

if (environment.offline) {
  WATSON_PROVIDERS.push([
    IndexedQueryService,
    IssueRemoteService,
    IssueMediaRemoteService,
    AssetRemoteService,
    InspectionRemoteService,
    NoteRemoteService,
    SavedSearchRemoteService,
    OutputSyncService,
    WarehouseSyncService,
    ObservableSyncService,
    LockingIndexedService,
    OutputSyncService,
    WarehouseSyncEventService,
    LayoutTextRemoteService,
    WarehouseOutlineRemoteService,
    SystemRemoteService,
    { provide: WarehouseSyncStepFactory, useClass: DefaultWarehouseSyncStepFactory },
    { provide: IndexedService, useFactory: () => new IdbService(true) },
    { provide: IssueCfgService, useClass: IdbIssueCfgService },
    { provide: IssueService, useClass: IdbIssueService },
    { provide: SavedSearchService, useClass: IdbSavedSearchService },
    { provide: ObservableSavedSearchService, useClass: IdbObservableSavedSearchService },
    { provide: ObservableAssetService, useClass: IdbObservableAssetService },
    { provide: ObservableIssueService, useClass: IdbObservableIssueService },
    { provide: ObservableRepairService, useClass: FirebaseObservableRepairService },
    { provide: ObservableIssueMediaService, useClass: IdbObservableIssueMediaService },
    { provide: AssetService, useClass: IdbAssetService },
    { provide: InspectionService, useClass: IdbInspectionService },
    { provide: NoteService, useClass: IdbNoteService },
    { provide: WarehouseService, useClass: IdbWarehouseService },
    { provide: WarehouseOutlineService, useClass: IdbWarehouseOutlineService },
    { provide: WarehouseLayoutTextService, useClass: IdbWarehouseLayoutTextService },
    { provide: SystemDrawingService, useClass: IdbSystemDrawingService },
    { provide: UprightService, useClass: IdbUprightService },
    { provide: ObservableInspectionService, useClass: IdbObservableInspectionService },
    { provide: ObservableNoteService, useClass: IdbObservableNoteService },
  ]);
} else {
  WATSON_PROVIDERS.push([
    { provide: WarehouseSyncEventService, useFactory: () => null },
    { provide: IssueCfgService, useClass: FirebaseIssueCfgService },
    { provide: IssueService, useClass: FirebaseIssueService },
    { provide: SavedSearchService, useClass: FirebaseSavedSearchService },
    { provide: ObservableSavedSearchService, useClass: FirebaseObservableSavedSearchService },
    { provide: ObservableAssetService, useClass: FirebaseObservableAssetService },
    { provide: ObservableIssueService, useClass: FirebaseObservableIssueService },
    { provide: ObservableRepairService, useClass: FirebaseObservableRepairService },
    { provide: ObservableIssueMediaService, useClass: FirebaseObservableIssueMediaService },
    { provide: ObservableRepairMediaService, useClass: FirebaseObservableRepairMediaService },
    { provide: AssetService, useClass: FirebaseAssetService },
    { provide: InspectionService, useClass: FirebaseInspectionService },
    { provide: NoteService, useClass: FirebaseNoteService },
    { provide: WarehouseService, useClass: FirebaseWarehouseService },
    { provide: WarehouseOutlineService, useClass: FirebaseWarehouseOutlineService },
    { provide: WarehouseLayoutTextService, useClass: FirebaseWarehouseLayoutTextService },
    { provide: SystemDrawingService, useClass: FirebaseSystemDrawingService },
    { provide: UprightService, useClass: FirebaseUprightService },
    { provide: ObservableInspectionService, useClass: FirebaseObservableInspectionService },
    { provide: ObservableNoteService, useClass: FirebaseObservableNoteService },
  ]);
}

// SC-9665 historical warehouse states. Gated so production ships without them. The payload is
// fetched on demand over HTTP and held in memory only (SnapshotSessionStore), never in IndexedDB.
// With these absent, WarehouseSourceService resolves them to null and stays on the current source.
const SNAPSHOT_PROVIDERS: Provider[] = [{ provide: SnapshotService, useClass: FirebaseWarehouseStateSnapshotService }, SnapshotSessionStore, SnapshotWarehouseDataSource];

if (environment.offline && environment.appConfig.watson.warehouseStateSnapshots) {
  WATSON_PROVIDERS.push([
    ...SNAPSHOT_PROVIDERS,
    // Same read-through wrappers as online, over the Idb implementations: the current state keeps
    // coming from IndexedDB, and nothing read from a snapshot is ever written back to it.
    { provide: AssetService, useClass: HistoryAwareIdbAssetService },
    { provide: IssueService, useClass: HistoryAwareIdbIssueService },
    { provide: ObservableAssetService, useClass: HistoryAwareIdbObservableAssetService },
    { provide: ObservableIssueService, useClass: HistoryAwareIdbObservableIssueService },
    { provide: ObservableIssueMediaService, useClass: HistoryAwareIdbObservableIssueMediaService },
    { provide: ObservableNoteService, useClass: HistoryAwareIdbObservableNoteService },
    { provide: SystemDrawingService, useClass: HistoryAwareIdbSystemDrawingService },
    { provide: UprightService, useClass: HistoryAwareIdbUprightService },
    { provide: WarehouseLayoutTextService, useClass: HistoryAwareIdbWarehouseLayoutTextService },
    { provide: WarehouseOutlineService, useClass: HistoryAwareIdbWarehouseOutlineService },
    { provide: WarehouseService, useClass: HistoryAwareIdbWarehouseService },
  ]);
}

if (!environment.offline && environment.appConfig.watson.warehouseStateSnapshots) {
  WATSON_PROVIDERS.push([
    ...SNAPSHOT_PROVIDERS,
    // Read-through wrappers: while a snapshot is selected these serve it instead of Firestore, so
    // the preview panels, grids and plan views follow the canvas to the same point in time.
    // They subclass the Firebase implementations, so writes are untouched.
    { provide: AssetService, useClass: HistoryAwareAssetService },
    { provide: IssueService, useClass: HistoryAwareIssueService },
    { provide: ObservableAssetService, useClass: HistoryAwareObservableAssetService },
    { provide: ObservableIssueService, useClass: HistoryAwareObservableIssueService },
    { provide: ObservableIssueMediaService, useClass: HistoryAwareObservableIssueMediaService },
    { provide: ObservableNoteService, useClass: HistoryAwareObservableNoteService },
    { provide: SystemDrawingService, useClass: HistoryAwareSystemDrawingService },
    { provide: UprightService, useClass: HistoryAwareUprightService },
    { provide: WarehouseLayoutTextService, useClass: HistoryAwareWarehouseLayoutTextService },
    { provide: WarehouseOutlineService, useClass: HistoryAwareWarehouseOutlineService },
    { provide: WarehouseService, useClass: HistoryAwareWarehouseService },
  ]);
}

@NgModule({ declarations: [], imports: [CommonModule], providers: [] })
export class ApiModule {
  static forRoot(): ModuleWithProviders<ApiModule> {
    return {
      ngModule: ApiModule,
      providers: [...WATSON_PROVIDERS, ...PRODUCTION_PROVIDERS, ...STAX_PROVIDERS, ...SECURITY_PROVIDERS, ...COMMON_PROVIDERS, ...REPOS_PROVIDERS, ...STORAGE_PROVIDERS],
    };
  }
}
